import { useState } from 'react'

import { Divider } from '@mantine/core'

import { navigate, routes } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import DNButton from 'src/components/Buttons/Button/DNButton'

import AppointmentsList from './components/AppointmentsList'
import DashboardHeader from './components/DashboardHeader'
import DocumentChecklist from './components/DocumentChecklist'
import ProspectChecklist from './components/ProspectChecklist'
import ProspectStatistics from './components/ProspectStatistics'
import TaskList from './components/TaskList'

const DashboardPage = () => {
  const { currentUser } = useAuth()
  const fullName = `${currentUser?.firstName} ${currentUser?.lastName}`
  const [selectedRecruiters, setSelectedRecruiters] = useState(() => {
    const id = currentUser?.employeeId
    const name = `${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`.trim()
    return id ? [{ label: name, value: id }] as Array<{ label: string; value: string }> : []
  })

  const onMetricClick = (key, value) => {
    // Create a clean URLSearchParams with only the filters we want to pass
    const queryParams = new URLSearchParams()
    queryParams.set(key, value)

    // When clicking on steps from the prospect checklist, also filter for prospect applicants
    if (key === 'steps') {
      queryParams.set('applicantStages', 'PROSPECT')
    }

    const recruiterIds = selectedRecruiters
      .map((recruiter) => recruiter.value)
      .join(',')
    if (recruiterIds.length > 0) {
      queryParams.set('recruiters', recruiterIds)
    }
    navigate(`/applicants?${queryParams.toString()}`)
  }

  // Create a new date object for today, but reset the time to midnight.
  const todayAtMidnight = new Date()
  todayAtMidnight.setHours(0, 0, 0, 0)

  // Calculate 30 days ago from the above date, also at midnight.
  const timeAgo = new Date(todayAtMidnight)
  timeAgo.setDate(todayAtMidnight.getDate() - 30)

  return (
    <div className="flex h-full flex-row gap-4 px-5 pb-5 ">
      <div className="flex h-full w-full flex-1 flex-col gap-5">
        <div className="flex flex-row items-start gap-3">
          {fullName && (
            <div className="min-w-0 flex-1">
              <DashboardHeader
                name={fullName}
                selectedRecruiters={selectedRecruiters}
                setSelectedRecruiters={setSelectedRecruiters}
              />
            </div>
          )}
          {/* Internal-only entry point to the agenda dashboard preview. This is
              deliberately the only way in — there is no sidebar link. */}
          <AdminContainer>
            <DNButton
              variant="outline"
              className="flex-shrink-0"
              onClick={() => navigate(routes.agendaDashboard())}
            >
              New dashboard (beta)
            </DNButton>
          </AdminContainer>
        </div>
        <div className="flex h-full flex-row gap-4 overflow-hidden">
          <div className="flex flex-1 flex-col gap-3">
            <div className="flex flex-row items-center gap-3">
              <TaskList selectedRecruiters={selectedRecruiters} />
              <ProspectStatistics
                selectedRecruiters={selectedRecruiters}
                onMetricClick={onMetricClick}
                timeAgo={timeAgo}
              />
            </div>
            <div className="flex min-h-0 flex-1 flex-row items-stretch gap-3">
              <ProspectChecklist
                selectedRecruiters={selectedRecruiters}
                onMetricClick={onMetricClick}
              />
              <DocumentChecklist
                recruiters={selectedRecruiters.map(
                  (recruiter) => recruiter.value
                )}
                onMetricClick={onMetricClick}
              />
            </div>
          </div>
          <Divider orientation="vertical" />
          <div className="w-[25%]">
            <AppointmentsList
              recruiters={selectedRecruiters.map(
                (recruiter) => recruiter.value
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
